import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontalIcon } from 'lucide-react';
import {
  ArticleEditor,
  Button,
  ChoiceList,
  ConfirmDialog,
  Dropzone,
  ErrorNotice,
  Field,
  Heading,
  LoadingState,
  MultiCombobox,
  PageHeading,
  Panel,
  SegmentedControl,
  SelectInput,
  TextArea,
  TextInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  RESOURCES_QUERY_KEY,
  RESOURCE_ACTION,
  RESOURCE_AUDIENCE,
  RESOURCE_FILE_ACCEPT,
  RESOURCE_FILE_MAX_BYTES,
  RESOURCE_FORMAT,
  RESOURCE_FORMAT_OPTIONS,
  RESOURCE_LIMITS,
  RESOURCE_STATUS,
  RESOURCE_TOPICS_QUERY_KEY,
  changeResourceStatus,
  createResource,
  draftOfResource,
  emptyResourceDraft,
  fileSize,
  inputOfDraft,
  listClientOrganizations,
  listResourceTopics,
  listResources,
  organizationResourcesQueryKey,
  previewPath,
  readResource,
  resourceQueryKey,
  saveProblems,
  shortDate,
  updateResource,
  uploadResourceFile,
  type ClientOrganization,
  type ResourceAction,
  type ResourceAudienceScope,
  type ResourceDetail,
  type ResourceDraft,
  type ResourceFormat,
} from '@/lib/resources';

/** The query parameter that opens the editor for one client (the organization tab's "Add a resource for …"). */
export const FOR_ORGANIZATION_PARAM = 'organization';

const CLIENTS_QUERY_KEY = ['resource-clients'] as const;

/**
 * Administration → Resources → one resource (Roy's portal list, item 13): a
 * full page, since an article needs room to write. New resources start here
 * too, at /resources/new.
 *
 * @returns The page.
 */
export function ResourceEditorPage(): React.ReactElement {
  const { resourceId } = useParams();
  const isNew = resourceId === undefined || resourceId === 'new';
  const resource = useQuery({
    queryKey: resourceQueryKey(resourceId ?? 'new'),
    queryFn: ({ signal }) => readResource(resourceId ?? '', signal),
    enabled: !isNew,
  });

  if (isNew) return <ResourceEditor key="new" resource={null} />;
  if (resource.isError) return <ErrorNotice title="Resource unavailable" message={errorMessage(resource.error)} />;
  if (resource.isPending) return <LoadingState label="Loading the resource…" />;
  // Keyed by id so the form starts over when another resource opens.
  return <ResourceEditor key={resource.data.id} resource={resource.data} />;
}

/** The status line under the title. */
function statusLine(resource: ResourceDetail | null): string {
  if (resource === null) return 'New resource. It stays a draft, seen only by BCE, until you publish it.';
  switch (resource.status) {
    case RESOURCE_STATUS.draft:
      return `Draft · saved ${shortDate(resource.updatedAt)}`;
    case RESOURCE_STATUS.published:
      return `Published ${shortDate(resource.publishedAt)} · updated ${shortDate(resource.updatedAt)}`;
    case RESOURCE_STATUS.archived:
      return 'Archived. Clients no longer see it. Restore it to edit or publish it again.';
    default: {
      const never: never = resource.status;
      return never;
    }
  }
}

/** The client whose portal Preview opens: the first chosen client, else the first client. */
function previewClient(draft: ResourceDraft, clients: readonly ClientOrganization[]): ClientOrganization | null {
  if (draft.audience === RESOURCE_AUDIENCE.chosenClients) {
    return clients.find((client) => client.id === draft.organizationIds[0]) ?? null;
  }
  return clients[0] ?? null;
}

/** Secondary actions behind "More": unpublish and archive. */
function MoreMenu({
  items,
}: {
  readonly items: readonly { readonly label: string; readonly onSelect: () => void }[];
}): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  if (items.length === 0) return null;
  return (
    <div ref={rootRef} className="relative">
      <Button variant="secondary" aria-label="More actions" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <MoreHorizontalIcon aria-hidden className="size-4" />
      </Button>
      {open ? (
        <div role="menu" className="absolute right-0 z-20 mt-1 min-w-[12rem] rounded-portal-card border border-portal-line bg-white py-1 shadow-portal-raised">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setOpen(false);
              }}
              className="block w-full px-4 py-2.5 text-left text-portal-small hover:bg-portal-sky"
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** A field's hint, or what is wrong with it once someone has tried to save. */
function hintFor(problem: string | undefined, fallback: string | undefined, touched: boolean): string | undefined {
  return touched && problem !== undefined ? problem : fallback;
}

/** What a confirmation asks before a status change. */
const CONFIRMATIONS: Partial<Record<ResourceAction, { title: string; message: string; confirmLabel: string }>> = {
  [RESOURCE_ACTION.archive]: {
    title: 'Archive this resource?',
    message: 'It leaves every client’s Resource center at once. Its record and its counts stay, and you can restore it later.',
    confirmLabel: 'Archive',
  },
  [RESOURCE_ACTION.unpublish]: {
    title: 'Unpublish this resource?',
    message: 'Clients stop seeing it right away. It goes back to a draft you can publish again.',
    confirmLabel: 'Unpublish',
  },
};

function ResourceEditor({ resource }: { readonly resource: ResourceDetail | null }): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const forOrganization = resource === null ? params.get(FOR_ORGANIZATION_PARAM) : null;
  const backTo = params.get(FOR_ORGANIZATION_PARAM) ? `/organizations/${params.get(FOR_ORGANIZATION_PARAM) ?? ''}?tab=resources` : '/resources';

  const [draft, setDraft] = useState<ResourceDraft>(() => (resource ? draftOfResource(resource) : emptyResourceDraft(forOrganization)));
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState<ResourceAction | null>(null);

  const topics = useQuery({ queryKey: RESOURCE_TOPICS_QUERY_KEY, queryFn: ({ signal }) => listResourceTopics(signal) });
  const clients = useQuery({ queryKey: CLIENTS_QUERY_KEY, queryFn: ({ signal }) => listClientOrganizations(signal), staleTime: 5 * 60_000 });
  const library = useQuery({ queryKey: RESOURCES_QUERY_KEY, queryFn: ({ signal }) => listResources(signal) });

  const status = resource?.status ?? RESOURCE_STATUS.draft;
  const archived = status === RESOURCE_STATUS.archived;
  const published = status === RESOURCE_STATUS.published;
  const problems = saveProblems(draft);
  const canSave = Object.keys(problems).length === 0;
  const set = <K extends keyof ResourceDraft>(key: K, value: ResourceDraft[K]): void => setDraft((current) => ({ ...current, [key]: value }));

  const refresh = async (saved: ResourceDetail): Promise<void> => {
    queryClient.setQueryData(resourceQueryKey(saved.id), saved);
    await queryClient.invalidateQueries({ queryKey: RESOURCES_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: RESOURCE_TOPICS_QUERY_KEY });
    for (const organization of saved.audience.organizations) {
      await queryClient.invalidateQueries({ queryKey: organizationResourcesQueryKey(organization.id) });
    }
  };

  /** Saves the form (creating the resource the first time) and uploads a chosen file. */
  async function persist(): Promise<ResourceDetail> {
    const input = inputOfDraft(draft);
    let saved = resource === null ? await createResource(input) : await updateResource(resource.id, input);
    if (pendingFile !== null && draft.format === RESOURCE_FORMAT.file) {
      saved = await uploadResourceFile(saved.id, pendingFile);
      setPendingFile(null);
    }
    return saved;
  }

  const save = useMutation({
    mutationFn: async (then: ResourceAction | null) => {
      const saved = await persist();
      return then === null ? saved : changeResourceStatus(saved.id, then);
    },
    onSuccess: async (saved) => {
      await refresh(saved);
      if (resource === null) navigate(`/resources/${saved.id}${forOrganization ? `?${FOR_ORGANIZATION_PARAM}=${forOrganization}` : ''}`, { replace: true });
    },
  });
  const transition = useMutation({
    mutationFn: (action: ResourceAction) => changeResourceStatus(resource?.id ?? '', action),
    onSuccess: async (saved) => {
      setConfirming(null);
      await refresh(saved);
    },
  });
  const upload = useMutation({
    mutationFn: (file: File) => uploadResourceFile(resource?.id ?? '', file),
    onSuccess: refresh,
  });

  function attempt(then: ResourceAction | null): void {
    setTouched(true);
    if (canSave) save.mutate(then);
  }

  function chooseFile(file: File): void {
    if (file.size > RESOURCE_FILE_MAX_BYTES) {
      setFileProblem('That file is over 25 MB. Share video as a link instead.');
      return;
    }
    setFileProblem(null);
    // A saved file resource uploads now; a new one uploads when it is first saved.
    if (resource !== null && resource.format === RESOURCE_FORMAT.file) upload.mutate(file);
    else setPendingFile(file);
  }

  const clientList = clients.data ?? [];
  const preview = resource !== null && !archived ? previewClient(draft, clientList) : null;
  const topicOptions = (topics.data ?? []).filter((topic) => !topic.archived || topic.id === draft.topicId);
  const tagOptions = [...new Set((library.data ?? []).flatMap((item) => item.tags))].sort().map((tag) => ({ value: tag, label: tag }));
  const firstClient = clientList.find((client) => client.id === draft.organizationIds[0])?.name ?? 'the client';
  const working = save.isPending || transition.isPending;

  const moreItems = archived
    ? []
    : [
        ...(published ? [{ label: 'Unpublish', onSelect: () => setConfirming(RESOURCE_ACTION.unpublish) }] : []),
        ...(resource !== null ? [{ label: 'Archive', onSelect: () => setConfirming(RESOURCE_ACTION.archive) }] : []),
      ];
  const confirmation = confirming === null ? undefined : CONFIRMATIONS[confirming];

  return (
    <section className="animate-portal-rise">
      <PageHeading
        backTo={backTo}
        breadcrumbs={[{ label: 'Administration' }, { label: 'Resources', to: '/resources' }]}
        title={resource?.title ?? (draft.title.trim() || 'New resource')}
        subtitle={statusLine(resource)}
        size="compact"
        action={
          archived ? (
            <Button disabled={transition.isPending} onClick={() => transition.mutate(RESOURCE_ACTION.restore)}>
              {transition.isPending ? 'Restoring…' : 'Restore as a draft'}
            </Button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {preview !== null && resource !== null ? (
                <Button guide="resource-preview" variant="secondary" href={previewPath(preview.portalUrl, resource.slug)} target="_blank" rel="noreferrer">
                  Preview in {preview.name}’s portal
                </Button>
              ) : null}
              {published ? (
                <Button disabled={working} onClick={() => attempt(null)}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              ) : (
                <>
                  <Button guide="resource-save-draft" variant="secondary" disabled={working} onClick={() => attempt(null)}>
                    {save.isPending && save.variables === null ? 'Saving…' : 'Save draft'}
                  </Button>
                  <Button guide="resource-publish" disabled={working} onClick={() => attempt(RESOURCE_ACTION.publish)}>
                    {save.isPending && save.variables === RESOURCE_ACTION.publish ? 'Publishing…' : 'Publish'}
                  </Button>
                </>
              )}
              <MoreMenu items={moreItems} />
            </div>
          )
        }
      />

      {save.isError ? <ErrorNotice title="Not saved" message={errorMessage(save.error)} /> : null}
      {transition.isError ? <ErrorNotice title="That didn’t work" message={errorMessage(transition.error)} /> : null}
      {save.isSuccess && !save.isPending ? (
        <p role="status" className="mb-4 text-portal-small text-portal-muted">
          {save.data.status === RESOURCE_STATUS.published ? 'Saved. Clients see this version now.' : 'Saved as a draft.'}
        </p>
      ) : null}

      <fieldset disabled={archived} className="grid gap-5 min-[1100px]:grid-cols-[minmax(0,1fr)_380px]">
        <Panel className="p-6">
          <div className="space-y-5">
            <Field guide="resource-format" label="Format" htmlFor="resource-format">
              <SegmentedControl<ResourceFormat>
                label="Format"
                options={RESOURCE_FORMAT_OPTIONS}
                value={draft.format}
                onChange={(format) => set('format', format)}
              />
            </Field>
            <Field guide="resource-title" label="Title" htmlFor="resource-title" required hint={hintFor(problems.title, undefined, touched)}>
              <TextInput id="resource-title" maxLength={RESOURCE_LIMITS.title} value={draft.title} onChange={(event) => set('title', event.target.value)} />
            </Field>
            <Field
              guide="resource-summary"
              label="Summary"
              htmlFor="resource-summary"
              required
              hint={hintFor(problems.summary, 'One or two sentences. Shown on the card and in search results.', touched)}
            >
              <TextArea
                id="resource-summary"
                rows={3}
                maxLength={RESOURCE_LIMITS.summary}
                value={draft.summary}
                onChange={(event) => set('summary', event.target.value)}
              />
            </Field>

            {draft.format === RESOURCE_FORMAT.article ? (
              <Field guide="resource-article" label="Article" htmlFor="resource-article" required hint="Use section headings to break it up. Clients can print it or save it as a PDF.">
                <ArticleEditor id="resource-article" ariaLabel="Article" initialDoc={draft.body} onChange={(body) => set('body', body)} />
              </Field>
            ) : null}

            {draft.format === RESOURCE_FORMAT.file ? (
              <Field label="File" htmlFor="resource-file" required hint={fileProblem ?? 'A PDF, Word, PowerPoint or Excel file, or an image, up to 25 MB. Share video as a link.'}>
                <div className="space-y-3">
                  {resource?.file && resource.format === RESOURCE_FORMAT.file ? (
                    <p className="rounded-portal-input border border-portal-line bg-portal-bg px-3 py-2 text-portal-small">
                      <b>{resource.file.name}</b>
                      <span className="text-portal-muted">
                        {' · '}
                        {resource.formatLine}
                        {' · '}
                        {fileSize(resource.file.size)}
                      </span>
                    </p>
                  ) : null}
                  {pendingFile !== null ? (
                    <p className="text-portal-small">
                      <b>{pendingFile.name}</b> <span className="text-portal-muted">uploads when you save.</span>
                    </p>
                  ) : null}
                  <Dropzone
                    accept={RESOURCE_FILE_ACCEPT}
                    label={resource?.file ? 'Drop a new file to replace it, or click to choose' : 'Drop the file here, or click to choose'}
                    hint="PDF, Word, PowerPoint, Excel or an image"
                    uploading={upload.isPending}
                    onFile={chooseFile}
                  />
                  {upload.isError ? <ErrorNotice inline title="File not uploaded" message={errorMessage(upload.error)} /> : null}
                </div>
              </Field>
            ) : null}

            {draft.format === RESOURCE_FORMAT.link ? (
              <Field label="Link" htmlFor="resource-url" required hint={hintFor(problems.url, 'A full https:// address. Clients open it in a new tab.', touched || problems.url !== undefined)}>
                <TextInput
                  id="resource-url"
                  type="url"
                  maxLength={RESOURCE_LIMITS.url}
                  placeholder="https://"
                  value={draft.url}
                  onChange={(event) => set('url', event.target.value)}
                />
              </Field>
            ) : null}
          </div>
        </Panel>

        <div className="space-y-5">
          <Panel guide="resource-audience" className="p-6">
            <Heading variant="card" className="mb-4">
              Who sees it
            </Heading>
            <ChoiceList<ResourceAudienceScope>
              label="Who sees this resource"
              value={draft.audience}
              onChange={(audience) => set('audience', audience)}
              options={[
                { key: RESOURCE_AUDIENCE.allClients, label: 'Every client' },
                { key: RESOURCE_AUDIENCE.chosenClients, label: 'Chosen clients' },
              ]}
              renderSelected={(key) =>
                key === RESOURCE_AUDIENCE.chosenClients ? (
                  clients.isError ? (
                    <ErrorNotice inline title="Clients didn’t load" message={errorMessage(clients.error)} />
                  ) : (
                    <MultiCombobox
                      id="resource-clients"
                      placeholder={clients.isPending ? 'Loading clients…' : 'Type to find a client'}
                      disabled={clients.isPending}
                      options={clientList.map((client) => ({ value: client.id, label: client.name }))}
                      values={draft.organizationIds}
                      onChange={(values) => set('organizationIds', values)}
                    />
                  )
                ) : null
              }
            />
            <p className="mt-3 text-portal-micro text-portal-muted">
              {touched && problems.organizationIds !== undefined
                ? problems.organizationIds
                : draft.audience === RESOURCE_AUDIENCE.chosenClients
                  ? `Chosen clients see it under “For ${firstClient}” at the top of their Resource center.`
                  : 'Every client portal shows it in the Resource center.'}
            </p>
          </Panel>

          <Panel className="p-6">
            <Heading variant="card" className="mb-4">
              Where it’s filed
            </Heading>
            <div className="space-y-4">
              <Field guide="resource-topic" label="Topic" htmlFor="resource-topic" required hint={hintFor(problems.topicId, undefined, touched)}>
                <SelectInput id="resource-topic" value={draft.topicId} onChange={(event) => set('topicId', event.target.value)}>
                  <option value="">Choose a topic</option>
                  {topicOptions.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.name}
                    </option>
                  ))}
                </SelectInput>
              </Field>
              <Field guide="resource-tags" label="Tags" htmlFor="resource-tags" hint="Words people might search for.">
                <MultiCombobox
                  id="resource-tags"
                  allowNew
                  placeholder="Type a tag"
                  options={tagOptions}
                  values={draft.tags}
                  onChange={(values) => set('tags', values.slice(0, RESOURCE_LIMITS.tags))}
                />
              </Field>
            </div>
          </Panel>
        </div>
      </fieldset>

      <ConfirmDialog
        open={confirmation !== undefined}
        title={confirmation?.title ?? ''}
        message={confirmation?.message ?? ''}
        confirmLabel={confirmation?.confirmLabel ?? 'Confirm'}
        tone="danger"
        pending={transition.isPending}
        onConfirm={() => {
          if (confirming !== null) transition.mutate(confirming);
        }}
        onCancel={() => setConfirming(null)}
      />
    </section>
  );
}
